import React, { FC, useState, useEffect } from "react";
import {
  ClientValue,
  CreateProposal,
  MatterTitleScreen,
  ClientFilterData,
  OnClientSave,
  MatterFormMode,
  MatterTitleScreenTabKey,
  AccruedFee,
  MatterDetailsScreen,
  CreateProposalScreens,
  MatterFilter,
  ExternalMatter,
  BaseAccruedFee,
  TitleInputState,
  DeprecatedRatesScreen,
  RelevantMatter,
} from "@design-system";
import { FormInstance, Layout } from "antd";
import { Permission, usePermissionCheck } from "../hooks";
import { useQuery, useLazyQuery, useSubscription } from "@apollo/client";
import { useLocation, useNavigate } from "react-router-dom";
import { CREATE_MATTER, CREATE_CLIENT } from "../graphql/mutation";
import { FIND_PARENT_MATTER_QUERY } from "~/containers/parent-matter/parent-matter-query";
import {
  ORGANISATIONAL_UNITS_QUERY,
  FILTERED_CLIENTS_QUERY,
  BEDROCK_CHECK_MATTER_TITLE_IS_DESCRIPTIVE,
} from "../graphql/query";
import {
  BEDROCK_START_STREAMING_CLARIFYING_QUESTIONS,
  STREAMING_CLARIFYING_QUESTIONS_SUBSCRIPTION,
} from "../graphql/query/bedrock-clarifying-questions-query";
import {
  BEDROCK_START_STREAMING_MATTER_SUMMARY,
  STREAMING_MATTER_SUMMARY_SUBSCRIPTION,
} from "../graphql/query/bedrock-generate-matter-summary-query";
import { useMutation, useSettings } from "../hooks";
import {
  CreateClientMutation,
  CreateClientMutationVariables,
  FindFilteredClientsQuery,
  FindFilteredClientsQueryVariables,
  FindOrganisationalUnitsQuery,
  RateReplacement,
} from "~/gql/graphql";
import { ExternalMatterStatus } from "../graphql/types";
import { useCreateMultiDisc } from "~/graphql/mutation/create-parent-matter";
import { useExternalMattersQueryLazy } from "../hooks/external-matters";
import { useFilteredClientsLazy } from "../hooks/clients";
import { FeeEarnerCascaderContainer } from "./fee-earner-cascader-container";
import { useRelevantMatterSearch, useRelevantExternalMatterSearch } from "../hooks/matters";
import styles from "./create-proposal-container.css";
import { useDebouncedCallback } from "use-debounce";

const {
  layout: layoutClassName,
  content: contentClassName,
  container: containerClassName,
} = styles;

export type SearchFlowState =
  | "idle"
  | "questions_answering"
  | "generating_summary"
  | "waiting_for_search_results_with_summary"
  | "waiting_for_search_results_without_summary"
  | "search_with_summary"
  | "search_without_summary";
const INITIAL_MATTER_FILTER: MatterFilter = {
  title: null,
  client: null,
  orgUnit: null,
  status: null,
  updatedAt: null,
  orderBy: null,
  createdBy: null,
  currencyId: null,
  feeEarner: null,
  someTimeEntries: true,
  linked: null,
};

export const CreateProposalContainer: FC<{
  parentMatterId?: number;
  client?: ClientValue;
}> = ({ parentMatterId, client }) => {
  const [matterTitle, setMatterTitle] = useState("");
  const [searchText, setSearchText] = useState("");
  const [additionalSearchText, setAdditionalSearchText] = useState("");
  const [selectedMatter, setSelectedMatter] = useState<RelevantMatter | null>(null);
  const [selectedExternalMatter, setSelectedExternalMatter] = useState<ExternalMatter | null>(null);
  const [selectedAccruedFees, setSelectedAccruedFees] = useState<AccruedFee | null>(null);
  const [selectedExternalAccruedFees, setSelectedExternalAccruedFees] =
    useState<BaseAccruedFee | null>(null);
  const [activeTab, setActiveTab] = useState<MatterTitleScreenTabKey>(
    MatterTitleScreenTabKey.TemplateMatters
  );
  const [isMatterTitleDescriptive, setIsMatterTitleDescriptive] = useState<boolean | undefined>(
    undefined
  );
  const [clarifyingQuestionAnswers, setClarifyingQuestionAnswers] = useState<
    Record<string, string[]>
  >({});
  const [generatedSummary, setGeneratedSummary] = useState<string>("");

  const [searchFlowState, setSearchFlowState] = useState<SearchFlowState>("idle");

  // Streaming state for clarifying questions
  const [streamingState, setStreamingState] = useState<{
    sessionId: string | null;
    streamingQuestions: any[];
    totalQuestions: number | null;
    isComplete: boolean;
    error: string | null;
    streamingActive: boolean;
  }>({
    sessionId: null,
    streamingQuestions: [],
    totalQuestions: null,
    isComplete: false,
    error: null,
    streamingActive: false,
  });

  // Streaming state for matter summary
  const [summaryStreamingState, setSummaryStreamingState] = useState<{
    sessionId: string | null;
    chunks: string[];
    fullSummary: string | null;
    isComplete: boolean;
    error: string | null;
    streamingActive: boolean;
  }>({
    sessionId: null,
    chunks: [],
    fullSummary: null,
    isComplete: false,
    error: null,
    streamingActive: false,
  });

  const [matterFilter, setMatterFilter] = useState<MatterFilter>(INITIAL_MATTER_FILTER);

  const { settings } = useSettings();
  const defaultCurrency = settings?.currency;
  const defaultCurrencyId = defaultCurrency?.id;
  useEffect(() => {
    if (defaultCurrencyId) {
      setMatterFilter({
        ...matterFilter,
        currencyId: [defaultCurrencyId],
      });
    }
  }, [defaultCurrencyId]);

  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [createMatter] = useMutation(CREATE_MATTER, {
    refetchQueries: (data) => {
      if (data.data?.createMatter.parent?.id) {
        return [
          {
            query: FIND_PARENT_MATTER_QUERY,
            variables: { id: data.data.createMatter.parent?.id },
          },
        ];
      }
      return [];
    },
    onCompleted: (data) => {
      if (data.createMatter.parent?.id && !pathname.includes("parent")) {
        return navigate(`/parent/${data.createMatter.parent?.id}`, { replace: true });
      }
      return navigate(`/matter-editor/${data.createMatter.id}`, { replace: true });
    },
    update: (cache, createdMatter) => {
      cache.evict({
        id: "ROOT_QUERY",
        fieldName: "filteredMatters",
      });

      if (createdMatter.data?.createMatter.externalMatterId) {
        cache.modify({
          id: `ExternalMatter:${createdMatter.data?.createMatter.externalMatterId}`,
          fields: {
            status() {
              return (
                createdMatter.data?.createMatter.externalMatter?.status ??
                ExternalMatterStatus.Unassigned
              );
            },
            matter() {
              return {
                id: createdMatter.data?.createMatter.id,
              };
            },
          },
        });
      }
    },
  });

  const [selectedClient, setSelectedClient] = useState<ClientValue | undefined | null>(client);

  useEffect(() => {
    setSelectedClient(client);
  }, [client]);

  const clientFilterData = useFilteredClientsLazy(10, true);

  const [createClient] = useMutation<CreateClientMutation, CreateClientMutationVariables>(
    CREATE_CLIENT,
    {
      update: (cache, mutationResult) => {
        const newClient = mutationResult.data?.createClient;
        if (!newClient) {
          return;
        }

        const data = cache.readQuery<FindFilteredClientsQuery, FindFilteredClientsQueryVariables>({
          query: FILTERED_CLIENTS_QUERY,
          variables: clientFilterData?.queryVars,
        });

        cache.writeQuery<FindFilteredClientsQuery, FindFilteredClientsQueryVariables>({
          query: FILTERED_CLIENTS_QUERY,
          variables: {
            ...clientFilterData?.queryVars,
          },
          data: {
            filteredClients: {
              clients: [...(data?.filteredClients?.clients || []), newClient],
              total: (data?.filteredClients?.total ?? 0) + 1,
              __typename: "ClientResult",
            },
          },
        });
        if (newClient?.name) {
          setSelectedClient({
            ...newClient,
          });
          clientFilterData.search(newClient?.name);
        }
      },
    }
  );

  const { data: organisationalUnitData } = useQuery<FindOrganisationalUnitsQuery>(
    ORGANISATIONAL_UNITS_QUERY
  );

  const {
    templatesData,
    templatesLoading,
    historicalData,
    historicalLoading,
    triggerSearch: triggerMatterSearch,
  } = useRelevantMatterSearch(matterTitle, "", matterFilter, {
    additionalSearchText: additionalSearchText?.trim() || undefined,
    autoRetry: true,
    fetchTemplates: true,
  });

  const {
    externalMattersData,
    externalMattersLoading,
    triggerSearch: triggerExternalMatterSearch,
  } = useRelevantExternalMatterSearch(matterTitle, "", matterFilter, {
    additionalSearchText: additionalSearchText?.trim() || undefined,
    autoRetry: true,
    unlinkedOnly: true,
  });
  const [checkMatterTitleIsDescriptive, { loading: checkMatterTitleIsDescriptiveLoading }] =
    useLazyQuery(BEDROCK_CHECK_MATTER_TITLE_IS_DESCRIPTIVE);

  // Streaming matter summary mutation and subscription
  const [startStreamingMatterSummary] = useMutation(BEDROCK_START_STREAMING_MATTER_SUMMARY, {
    onCompleted: (data) => {
      if (data.bedrockStartStreamingMatterSummary.success) {
        setSummaryStreamingState((prev) => ({
          ...prev,
          sessionId: data.bedrockStartStreamingMatterSummary.sessionId,
          streamingActive: true,
          isComplete: false,
          error: null,
          chunks: [],
          fullSummary: null,
        }));
      }
    },
    onError: (error) => {
      setSummaryStreamingState((prev) => ({
        ...prev,
        error: error.message,
        streamingActive: false,
      }));
      // If streaming fails, transition to search without summary
      setSearchFlowState("search_without_summary");
      // Trigger search without summary
      if (searchText.trim()) {
        debouncedFindMatters(searchText, matterFilter, additionalSearchText?.trim() || undefined);
      }
    },
  });

  useSubscription(STREAMING_MATTER_SUMMARY_SUBSCRIPTION, {
    variables: { sessionId: summaryStreamingState.sessionId! },
    skip: !summaryStreamingState.sessionId,
    onData: ({ data }) => {
      const event = data.data?.streamingMatterSummary;
      if (!event) return;

      setSummaryStreamingState((prev) => {
        switch (event.eventType) {
          case "chunk":
            const newChunks = [...prev.chunks, event.chunk || ""];
            return {
              ...prev,
              chunks: newChunks,
            };
          case "complete":
            return {
              ...prev,
              isComplete: true,
              streamingActive: false,
              fullSummary: event.fullSummary || prev.chunks.join(""),
            };
          case "error":
            return {
              ...prev,
              error: event.error,
              streamingActive: false,
            };
          default:
            return prev;
        }
      });
    },
  });

  // Streaming clarifying questions mutation and subscription
  const [startStreamingClarifyingQuestions] = useMutation(
    BEDROCK_START_STREAMING_CLARIFYING_QUESTIONS,
    {
      onCompleted: (data) => {
        if (data.bedrockStartStreamingClarifyingQuestions.success) {
          setStreamingState((prev) => ({
            ...prev,
            sessionId: data.bedrockStartStreamingClarifyingQuestions.sessionId,
            streamingActive: true,
            isComplete: false,
            error: null,
            streamingQuestions: [],
          }));
        }
      },
      onError: (error) => {
        setStreamingState((prev) => ({
          ...prev,
          error: error.message,
          streamingActive: false,
        }));
        // If streaming fails, transition to waiting for search results
        setSearchFlowState("waiting_for_search_results_without_summary");
        // Trigger search without summary
        if (searchText.trim()) {
          debouncedFindMatters(searchText, matterFilter, additionalSearchText?.trim() || undefined);
        }
      },
    }
  );

  useSubscription(STREAMING_CLARIFYING_QUESTIONS_SUBSCRIPTION, {
    variables: { sessionId: streamingState.sessionId! },
    skip: !streamingState.sessionId,
    onData: ({ data }) => {
      const event = data.data?.streamingClarifyingQuestions;
      if (!event) return;

      setStreamingState((prev) => {
        switch (event.eventType) {
          case "question":
            return {
              ...prev,
              streamingQuestions: [...prev.streamingQuestions, event.question],
            };
          case "metadata":
            return {
              ...prev,
              totalQuestions: event.totalQuestions,
            };
          case "complete":
            return {
              ...prev,
              isComplete: true,
              streamingActive: false,
              totalQuestions: event.totalQuestions,
            };
          case "error":
            return {
              ...prev,
              error: event.error,
              streamingActive: false,
            };
          default:
            return prev;
        }
      });
    },
  });

  const handleStartStreaming = (titleOverride?: string) => {
    const title = titleOverride || matterTitle;
    if (!title.trim()) return;

    // Reset streaming state and clear previous summary
    setStreamingState({
      sessionId: null,
      streamingQuestions: [],
      totalQuestions: null,
      isComplete: false,
      error: null,
      streamingActive: false,
    });
    setGeneratedSummary("");
    setClarifyingQuestionAnswers({});
    setProcessedQuestions(new Set());

    startStreamingClarifyingQuestions({
      variables: {
        matterTitle: title,
        countryCode: settings?.defaultCountryCode ?? null,
      },
    });
  };

  // Track which questions have been processed (either answered or skipped)
  const [processedQuestions, setProcessedQuestions] = useState<Set<string>>(new Set());

  // Helper function to convert answers to the required format
  const convertAnswersToQuestionResponses = (answers?: Record<string, string[]>) => {
    const questions = streamingState.streamingQuestions;
    const answersToUse = answers || clarifyingQuestionAnswers;
    return Object.entries(answersToUse)
      .map(([questionId, answerArray]) => {
        const question = questions.find((q: any) => q.id === questionId);
        if (!question || !answerArray || answerArray.length === 0) return null;

        // Separate predefined options from custom "other" values
        const predefinedAnswers: string[] = [];
        const predefinedLabels: string[] = [];
        const otherValues: string[] = [];

        answerArray.forEach((answer) => {
          const selectedOption = question.options?.find((opt: any) => opt.value === answer);
          if (selectedOption) {
            // It's a predefined option
            predefinedAnswers.push(answer);
            predefinedLabels.push(selectedOption.label);
          } else if (answer !== "other") {
            // It's a custom "other" value (not the literal "other" option)
            otherValues.push(answer);
          }
        });

        // If "other" was selected as an option, we should have custom values
        const hasOtherOption = answerArray.includes("other");

        return {
          questionId,
          questionLabel: question.label,
          selectedValues: predefinedAnswers,
          selectedLabels: predefinedLabels,
          ...(hasOtherOption && { otherValues }),
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);
  };

  // Generate summary when all questions are answered
  const handleGenerateSummaryFromAnswers = async (answersOverride?: Record<string, string[]>) => {
    const answersToUse = answersOverride || clarifyingQuestionAnswers;

    // Count only non-empty answer arrays
    const nonEmptyAnswers = Object.keys(answersToUse).filter(
      (key) => answersToUse[key] && answersToUse[key].length > 0
    );

    if (!matterTitle.trim() || nonEmptyAnswers.length === 0) {
      return;
    }

    setSearchFlowState("generating_summary");

    try {
      const questionResponses = convertAnswersToQuestionResponses(answersToUse);

      // Reset summary streaming state and clear previous summary
      setSummaryStreamingState({
        sessionId: null,
        chunks: [],
        fullSummary: null,
        isComplete: false,
        error: null,
        streamingActive: false,
      });
      setGeneratedSummary("");

      // Start streaming summary generation
      startStreamingMatterSummary({
        variables: {
          input: {
            matterTitle: matterTitle,
            questionResponses,
            countryCode: settings?.defaultCountryCode || "GB",
          },
        },
      });
    } catch (error) {
      console.error("Failed to start streaming matter summary:", error);
      // Error starting streaming - fall back to search without summary
      setSearchFlowState("search_without_summary");
      debouncedFindMatters(searchText, matterFilter, additionalSearchText?.trim() || undefined);
    }
  };

  const debouncedFindMatters = useDebouncedCallback(
    (searchText: string, filter: MatterFilter, additionalSearchText?: string) => {
      if (!searchText) return;

      // Update the hook's search parameters by triggering the search
      // The hooks will handle the actual search logic internally
      triggerMatterSearch();
      triggerExternalMatterSearch();

      // Extract the base matter title for the descriptive check
      const baseMatterTitle = searchText.split("\n")[0] || searchText;
      checkMatterTitleIsDescriptive({ variables: { matterTitle: baseMatterTitle } }).then(
        (data: any) => {
          setIsMatterTitleDescriptive(data.data?.bedrockCheckMatterTitleIsDescriptive);
        }
      );
    },
    300
  );

  // Handle completion of streaming summary
  useEffect(() => {
    if (summaryStreamingState.isComplete && searchFlowState === "generating_summary") {
      const finalSummary =
        summaryStreamingState.fullSummary || summaryStreamingState.chunks.join("");
      let newSearchText = matterTitle;
      if (finalSummary) {
        setGeneratedSummary(finalSummary);
        newSearchText += `\n\n${finalSummary}`;
        setSearchText(newSearchText);
      }
      // Transition to waiting for search results state and trigger search
      setSearchFlowState("waiting_for_search_results_with_summary");

      // Trigger search immediately with the new summary
      if (newSearchText.trim()) {
        debouncedFindMatters(
          newSearchText,
          matterFilter,
          additionalSearchText?.trim() || undefined
        );
      }
    }
  }, [
    summaryStreamingState.isComplete,
    summaryStreamingState.fullSummary,
    summaryStreamingState.chunks,
    searchFlowState,
  ]);

  // Handle streaming summary errors
  useEffect(() => {
    if (summaryStreamingState.error && searchFlowState === "generating_summary") {
      console.error("Streaming summary error:", summaryStreamingState.error);
      setSearchFlowState("waiting_for_search_results_without_summary");

      // Trigger search without summary
      if (searchText.trim()) {
        debouncedFindMatters(searchText, matterFilter, additionalSearchText?.trim() || undefined);
      }
    }
  }, [summaryStreamingState.error, searchFlowState]);

  // Handle search result completion - transition from waiting to final state
  useEffect(() => {
    if (
      (searchFlowState === "waiting_for_search_results_with_summary" ||
        searchFlowState === "waiting_for_search_results_without_summary") &&
      !templatesLoading &&
      templatesData // Ensure template matters have actually been loaded
    ) {
      // Template matters have completed loading, transition to final state
      if (generatedSummary) {
        setSearchFlowState("search_with_summary");
      } else {
        setSearchFlowState("search_without_summary");
      }
    }
  }, [searchFlowState, templatesLoading, templatesData, generatedSummary]);

  useEffect(() => {
    // Only trigger searches when in final search states (not when waiting for results)
    // The waiting_for_search_results searches are triggered explicitly by the transitions
    if (searchFlowState === "search_with_summary" || searchFlowState === "search_without_summary") {
      if (searchText.trim()) {
        debouncedFindMatters(searchText, matterFilter, additionalSearchText?.trim() || undefined);
      }
    }
  }, [searchText, additionalSearchText, searchFlowState]);

  // Handle cases where streaming completes but no questions received, or streaming times out
  useEffect(() => {
    if (
      searchFlowState === "questions_answering" &&
      streamingState.isComplete &&
      streamingState.streamingQuestions.length === 0
    ) {
      // No questions received, transition to waiting for search results
      setSearchFlowState("waiting_for_search_results_without_summary");
      if (searchText.trim()) {
        debouncedFindMatters(searchText, matterFilter, additionalSearchText?.trim() || undefined);
      }
    }
  }, [
    searchFlowState,
    streamingState.isComplete,
    streamingState.streamingQuestions.length,
    searchText,
    matterFilter,
    additionalSearchText,
  ]);

  // Timeout fallback - only if streaming starts but gets stuck without questions
  useEffect(() => {
    if (
      searchFlowState === "questions_answering" &&
      streamingState.streamingActive &&
      streamingState.streamingQuestions.length === 0
    ) {
      // Only timeout if streaming is active but no questions appear within 15 seconds
      const timeout = setTimeout(() => {
        console.warn("Clarifying questions streaming timeout - proceeding to search");
        setSearchFlowState("waiting_for_search_results_without_summary");
        if (searchText.trim()) {
          debouncedFindMatters(searchText, matterFilter, additionalSearchText?.trim() || undefined);
        }
      }, 15000); // 15 second timeout, only for streaming issues

      return () => clearTimeout(timeout);
    }
  }, [searchFlowState, streamingState.streamingActive, streamingState.streamingQuestions.length]);

  const handleFilterChange = (newFilters: MatterFilter) => {
    setMatterFilter(newFilters);
    // Only trigger search if in final search states (not while waiting for initial results)
    if (searchFlowState === "search_with_summary" || searchFlowState === "search_without_summary") {
      debouncedFindMatters(searchText, newFilters, additionalSearchText?.trim() || undefined);
    }
  };

  const externalMatterData = useExternalMattersQueryLazy();

  const canCreateMatter = usePermissionCheck(Permission.CREATE_MATTER);
  if (!canCreateMatter) {
    return null;
  }

  const handleSelectedMatterChange = (matter: RelevantMatter | null) => {
    setSelectedMatter(matter);
    setSelectedAccruedFees(
      historicalData?.findRelevantMatters?.accruedFees?.find(
        (fee) => fee.matterId === matter?.id
      ) || null
    );
    setSelectedExternalMatter(null);
    setSelectedExternalAccruedFees(null);
  };

  const handleSelectedExternalMatterChange = (matter: ExternalMatter | null) => {
    setSelectedExternalMatter(matter);
    setSelectedExternalAccruedFees(
      externalMattersData?.findRelevantExternalMatters?.accruedFees?.find(
        (fee) => fee.externalMatterId === matter?.id
      ) || null
    );
    setSelectedMatter(null);
    setSelectedAccruedFees(null);
  };

  // Wrapper function to handle matter title changes properly
  const handleMatterTitleChange = (newTitle: string, forceResend = false) => {
    setMatterTitle(newTitle);
    // Transition states based on title
    if (newTitle.trim()) {
      // Check if clarifying questions are enabled in settings
      if (settings?.showClarifyingQuestions ?? true) {
        setSearchFlowState("questions_answering");
        // Start streaming immediately when title is set
        if (forceResend || (!generatedSummary && !streamingState.streamingActive)) {
          handleStartStreaming(newTitle);
        }
      } else {
        // Skip clarifying questions and go straight to search
        setSearchFlowState("waiting_for_search_results_without_summary");
        setSearchText(newTitle);
        debouncedFindMatters(newTitle, matterFilter, additionalSearchText?.trim() || undefined);
      }
    } else {
      setSearchFlowState("idle");
      setGeneratedSummary("");
      setClarifyingQuestionAnswers({});
      setProcessedQuestions(new Set());
    }
  };

  const handleStartOver = (newTitle: string) => {
    handleMatterTitleChange(newTitle, true);
  };

  const clearRelevantMatters = () => {
    setMatterTitle("");
    setSearchText("");
    setSearchFlowState("idle");
    // The hooks will clear results when searchText is empty
    // No need to manually call queries anymore
  };

  const matterTitleScreen = (
    form: FormInstance,
    options: {
      uiState: TitleInputState;
      setUiState: (state: TitleInputState) => void;
    }
  ) => (
    <MatterTitleScreen
      form={form}
      templateMattersLoading={templatesLoading || false}
      historicalMattersLoading={historicalLoading}
      matterTitle={matterTitle}
      additionalSearchText={additionalSearchText}
      setAdditionalSearchText={setAdditionalSearchText}
      templateMatters={templatesData}
      historicalMatters={historicalData}
      historicalComparisonLoading={externalMattersLoading}
      externalMatters={externalMattersData}
      selectedMatter={selectedMatter}
      setSelectedMatter={handleSelectedMatterChange}
      selectedAccruedFees={selectedAccruedFees}
      selectedExternalMatter={selectedExternalMatter}
      setSelectedExternalMatter={handleSelectedExternalMatterChange}
      selectedExternalAccruedFees={selectedExternalAccruedFees}
      handleMatterTitleChange={handleMatterTitleChange}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      matterFilter={matterFilter}
      handleFilterChange={handleFilterChange}
      uiState={options.uiState}
      setUiState={options.setUiState}
      onStartOver={handleStartOver}
      showClarifyingQuestionsEnabled={settings?.showClarifyingQuestions ?? true}
      onAnswerSubmit={(questionId: string, answers: string[]) => {
        setClarifyingQuestionAnswers((prev) => {
          // Only store non-empty answer arrays
          const newAnswers = { ...prev };

          if (answers && answers.length > 0) {
            newAnswers[questionId] = answers;
          } else {
            // Remove empty answer arrays
            delete newAnswers[questionId];
          }

          return newAnswers;
        });

        // Mark this question as processed
        setProcessedQuestions((prev) => new Set([...prev, questionId]));

        // Check if this is the final question (free-form text with no options, just allowOther)
        const currentQuestion = streamingState.streamingQuestions.find(
          (q: any) => q.id === questionId
        );
        const isFinalQuestion =
          currentQuestion &&
          (!currentQuestion.options || currentQuestion.options.length === 0) &&
          currentQuestion.allowOther;

        if (isFinalQuestion && answers && answers.length > 0) {
          // Final question answered - check if we have any answers across all questions
          setTimeout(() => {
            // Get the current state of answers after this update
            setClarifyingQuestionAnswers((currentAnswers) => {
              const hasAnyAnswers = Object.keys(currentAnswers).length > 0;

              if (hasAnyAnswers) {
                // We have answers, generate summary
                handleGenerateSummaryFromAnswers(currentAnswers);
              } else {
                // No answers, skip summary generation
                setGeneratedSummary("");
                setSearchText(matterTitle);
                setSearchFlowState("waiting_for_search_results_without_summary");
                // Trigger search without summary
                debouncedFindMatters(
                  matterTitle,
                  matterFilter,
                  additionalSearchText?.trim() || undefined
                );
              }

              return currentAnswers;
            });
          }, 100);
        }
      }}
      clarifyingQuestionAnswers={clarifyingQuestionAnswers}
      onSkipQuestions={() => {
        setClarifyingQuestionAnswers({});
        setProcessedQuestions(new Set());
        setGeneratedSummary("");
        setSearchText(matterTitle);
        setSearchFlowState("waiting_for_search_results_without_summary");
        // Trigger search without summary
        debouncedFindMatters(matterTitle, matterFilter, additionalSearchText?.trim() || undefined);
      }}
      onSkipQuestion={(questionId: string) => {
        // Mark this question as processed but don't store any answer
        setProcessedQuestions((prev) => new Set([...prev, questionId]));

        // Check if this is the final question (free-form text with no options, just allowOther)
        const currentQuestion = streamingState.streamingQuestions.find(
          (q: any) => q.id === questionId
        );
        const isFinalQuestion =
          currentQuestion &&
          (!currentQuestion.options || currentQuestion.options.length === 0) &&
          currentQuestion.allowOther;

        if (isFinalQuestion) {
          // Final question skipped - check if we have any answers across all questions
          setTimeout(() => {
            const hasAnyAnswers = Object.keys(clarifyingQuestionAnswers).length > 0;

            if (hasAnyAnswers) {
              // We have some answers, generate summary
              handleGenerateSummaryFromAnswers();
            } else {
              // All questions were skipped, skip summary generation
              setGeneratedSummary("");
              setSearchText(matterTitle);
              setSearchFlowState("waiting_for_search_results_without_summary");
              // Trigger search without summary
              debouncedFindMatters(
                matterTitle,
                matterFilter,
                additionalSearchText?.trim() || undefined
              );
            }
          }, 100);
        }
      }}
      // Streaming props
      streamingState={streamingState}
      // Summary streaming props
      summaryStreamingState={summaryStreamingState}
      searchFlowState={searchFlowState}
    />
  );

  const matterDetailsScreen = (
    form: FormInstance,
    setScreen: (screen: CreateProposalScreens) => void
  ) => {
    return (
      <MatterDetailsScreen
        setScreen={setScreen}
        selectedMatter={selectedMatter}
        setSelectedMatter={setSelectedMatter}
        client={selectedClient}
        setCurrentClient={(client) => {
          setSelectedClient(client);
        }}
        template={"matter"}
        parentMatterId={parentMatterId}
        form={form}
        externalMatterData={externalMatterData}
        pmsName={settings?.organisationPmsName || ""}
        practiceAreas={organisationalUnitData?.organisationalUnits || []}
        onClientSave={
          ((values: any) => {
            createClient({ variables: { data: values as any } });
          }) as OnClientSave
        }
        clientFilterData={clientFilterData as ClientFilterData}
        feeEarnersCascaderRender={() => <FeeEarnerCascaderContainer showSearch />}
        mode={MatterFormMode.Create}
        selectedCurrencyId={matterFilter.currencyId?.[0] ?? undefined}
      />
    );
  };

  // Check if the selected historical matter has deprecated rates
  const hasDeprecatedRates =
    selectedMatter &&
    !selectedMatter.template &&
    selectedMatter.teamMemberships &&
    selectedMatter.teamMemberships.some((tm) => tm.rate.deprecated || tm.feeEarner?.deprecated);

  const deprecatedRatesScreen = (
    form: FormInstance,
    setScreen: (screen: CreateProposalScreens) => void,
    setRateReplacements: (replacements: RateReplacement[]) => void,
    setAllRatesReplaced: (allReplaced: boolean) => void
  ) => {
    if (!selectedMatter || !selectedMatter.teamMemberships) {
      return null;
    }

    const deprecatedRates = selectedMatter.teamMemberships.filter(
      (tm) => tm.rate.deprecated || tm.feeEarner?.deprecated
    );

    return (
      <DeprecatedRatesScreen
        form={form}
        setScreen={setScreen}
        setRateReplacements={setRateReplacements}
        setAllRatesReplaced={setAllRatesReplaced}
        deprecatedRates={deprecatedRates}
        matterId={selectedMatter.id}
        clientId={selectedMatter.client?.id}
        organisationalUnitId={selectedMatter.organisationalUnit?.id}
        currency={selectedMatter.currency || defaultCurrency || { code: "", symbol: "" }}
        pricingType={selectedMatter.pricingType}
      />
    );
  };

  return (
    <div className={containerClassName}>
      <Layout className={layoutClassName}>
        <Layout.Content className={contentClassName}>
          <CreateProposal
            parentMatterId={parentMatterId}
            onCreate={({
              templateId,
              historicalMatterId,
              copyScope,
              copyResourcing,
              copyPricing,
              practiceAreaId,
              parentPhaseId,
              rateReplacements,
              ...values
            }: any) => {
              createMatter({
                variables: {
                  createFromOptions: {
                    templateId: templateId || null,
                    historicalMatterId: historicalMatterId || null,
                    copyMatterDetails: {
                      copyScope: copyScope || false,
                      copyResourcing: copyResourcing || false,
                      copyPricing: copyPricing || false,
                    },
                    rateReplacements: rateReplacements || [],
                  },
                  parentId: parentMatterId || null,
                  parentPhaseId: parentPhaseId || null,
                  data: {
                    ...(practiceAreaId && {
                      organisationalUnit: { connect: { id: practiceAreaId } },
                    }),
                    ...(values?.clientId && {
                      client: { connect: { id: values.clientId } },
                    }),
                    ...(values.externalMatterId && {
                      externalMatter: { connect: { id: values.externalMatterId } },
                    }),
                    ...(values?.leadFeeEarnerId && {
                      feeEarner: { connect: { id: values.leadFeeEarnerId } },
                    }),
                    title: values.title,
                    status: values.status,
                    pricingType: values.pricingType,
                    pricingLevel: values.pricingLevel,
                    pricingTarget: values.pricingTarget,
                    ...(values.matterCurrencyId && {
                      currency: {
                        connect: {
                          id: values.matterCurrencyId,
                        },
                      },
                    }),
                    ...(values.officeLocationId && {
                      officeLocation: {
                        connect: {
                          id: values.officeLocationId,
                        },
                      },
                    }),
                    confidential: values.confidential,
                    aiGeneratedMatterSummary: summaryStreamingState.fullSummary,
                  },
                },
              });
            }}
            createMultiDiscMatter={() => {}}
            selectedMatter={selectedMatter}
            setSelectedMatter={setSelectedMatter}
            selectedExternalMatter={selectedExternalMatter}
            setSelectedExternalMatter={setSelectedExternalMatter}
            matterTitleScreen={matterTitleScreen}
            matterDetailsScreen={matterDetailsScreen}
            deprecatedRatesScreen={deprecatedRatesScreen}
            hasDeprecatedRates={!!hasDeprecatedRates}
            onCancel={() => {
              Promise.resolve().then(() => {
                setAdditionalSearchText("");
                setMatterFilter(INITIAL_MATTER_FILTER);
                clearRelevantMatters();
                setGeneratedSummary("");
                setClarifyingQuestionAnswers({});
                setProcessedQuestions(new Set());
                setStreamingState({
                  sessionId: null,
                  streamingQuestions: [],
                  totalQuestions: null,
                  isComplete: false,
                  error: null,
                  streamingActive: false,
                });
                setSummaryStreamingState({
                  sessionId: null,
                  chunks: [],
                  fullSummary: null,
                  isComplete: false,
                  error: null,
                  streamingActive: false,
                });
              });
            }}
            setActiveTab={setActiveTab}
          />
        </Layout.Content>
      </Layout>
    </div>
  );
};
